/**
 * Rascunho operacional do Resumo Diário (não existe no TOTVS).
 * Prefixo `wb_ops_` alinha com futuras tabelas operacionais no LB.
 */

import i18n from "../../utils/i18n";

export const WB_OPS_DAILY_SUMMARY_DRAFT_PREFIX = "wb_ops_daily_summary_draft:";

export const DS_STATUS = {
  DRAFT: "draft",
  READY: "ready",
  SENT: "sent"
};

export function draftStorageKey(classId, date, momentId) {
  return `${WB_OPS_DAILY_SUMMARY_DRAFT_PREFIX}${classId}::${date}::${momentId}`;
}

export function loadDraft(classId, date, momentId) {
  try {
    const raw = localStorage.getItem(draftStorageKey(classId, date, momentId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error(e);
    return null;
  }
}

export function saveDraft(draft) {
  if (!draft || !draft.classId || !draft.date || !draft.momentId) {
    return null;
  }
  const payload = {
    ...draft,
    updatedAt: new Date().toISOString()
  };
  localStorage.setItem(
    draftStorageKey(draft.classId, draft.date, draft.momentId),
    JSON.stringify(payload)
  );
  return payload;
}

/** Conceitos ordenados Ruim → Regular → Bom (sem vazio). */
export function orderedConceptIds(concepts) {
  const list = (concepts || []).filter(c => c && c.id != null && c.label);
  const rank = label => {
    const u = String(label).toUpperCase();
    if (u === "RUIM") return 0;
    if (u === "REGULAR") return 1;
    if (u === "BOM") return 2;
    return 99;
  };
  return [...list].sort((a, b) => rank(a.label) - rank(b.label)).map(c => c.id);
}

export function cycleConceptId(currentId, conceptIds, direction) {
  const ids = conceptIds || [];
  if (!ids.length) return null;
  const idx = ids.findIndex(id => String(id) === String(currentId));
  if (direction > 0) {
    if (idx < 0) return ids[0];
    if (idx >= ids.length - 1) return null;
    return ids[idx + 1];
  }
  if (idx < 0) return ids[ids.length - 1];
  if (idx === 0) return null;
  return ids[idx - 1];
}

export function computeStatus({
  students,
  axisIds,
  values,
  absent,
  forcedSent
}) {
  if (forcedSent) return DS_STATUS.SENT;
  const active = (students || []).filter(s => !absent[String(s.ra)]);
  if (!active.length) return DS_STATUS.READY;
  const allFilled = active.every(s =>
    axisIds.every(axisId => {
      const v = values[String(s.ra)] && values[String(s.ra)][axisId];
      return v != null && v !== "";
    })
  );
  return allFilled ? DS_STATUS.READY : DS_STATUS.DRAFT;
}

export function statusLabel(status) {
  if (status === DS_STATUS.SENT) return i18n.t("dailySummary.status.sent");
  if (status === DS_STATUS.READY) return i18n.t("dailySummary.status.ready");
  return i18n.t("dailySummary.status.draft");
}

/** Extrai CODOCORRENCIAGRUPO do id do eixo (`MEIO_TARDE-10` → `10`). */
export function grupoFromAxisId(axisId) {
  const parts = String(axisId || "").split("-");
  return parts.length ? parts[parts.length - 1] : null;
}

/**
 * Cruza card da API com rascunho local (`wb_ops_*`) para % e status reais.
 * Sem isso a lista fica em 0% enquanto o professor só tem draft no browser.
 */
export function enrichSummaryFromDraft(summary, classId) {
  if (!summary || !summary.moment) return summary;
  const draft = loadDraft(classId, summary.date, summary.moment.id);
  const studentsCount = summary.studentsCount || 0;
  const indexesCount = summary.indexesCount || 0;
  const serverFilled = Math.round((summary.filledPercent || 0) * 100) / 100;

  if (!draft || !draft.values) {
    return {
      ...summary,
      status: serverFilled >= 1 ? DS_STATUS.READY : DS_STATUS.DRAFT,
      filledPercent: serverFilled
    };
  }

  if (draft.status === DS_STATUS.SENT) {
    return {
      ...summary,
      status: DS_STATUS.SENT,
      filledPercent: 1,
      updatedAt: draft.updatedAt || summary.updatedAt
    };
  }

  const absent = draft.absent || {};
  let filledCells = 0;

  Object.keys(draft.values || {}).forEach(ra => {
    if (absent[ra]) return;
    Object.values(draft.values[ra] || {}).forEach(v => {
      if (v != null && v !== "") filledCells += 1;
    });
  });

  // Alunos com falta não entram no denominador (não precisam preencher eixos)
  const absentCount = Object.keys(absent).filter(ra => absent[ra]).length;
  const denomStudents = Math.max(studentsCount - absentCount, 0);
  const required = denomStudents * indexesCount;
  let draftFilled = 0;
  if (required > 0) draftFilled = filledCells / required;
  else if (studentsCount > 0 && absentCount >= studentsCount) draftFilled = 1;

  const filledPercent = Math.max(
    serverFilled,
    Math.round(draftFilled * 100) / 100
  );
  const status =
    draft.status === DS_STATUS.READY || filledPercent >= 1
      ? DS_STATUS.READY
      : DS_STATUS.DRAFT;

  return {
    ...summary,
    status,
    filledPercent,
    updatedAt: draft.updatedAt || summary.updatedAt,
    draft
  };
}

/** Monta payload de save a partir do rascunho local. */
export function buildSavePayloadFromDraft(draft, classId) {
  if (!draft) return null;
  const cells = [];
  const absences = [];
  const values = draft.values || {};
  const comments = draft.comments || {};
  const absent = draft.absent || {};

  Object.keys(absent).forEach(ra => {
    if (absent[ra]) absences.push({ ra });
  });

  Object.keys(values).forEach(ra => {
    if (absent[ra]) return;
    Object.keys(values[ra] || {}).forEach(axisId => {
      const conceptId = values[ra][axisId];
      if (conceptId == null || conceptId === "") return;
      const grupo = grupoFromAxisId(axisId);
      if (!grupo) return;
      cells.push({
        ra,
        codOcorrenciaGrupo: grupo,
        conceptId,
        observacoes: (comments[ra] && comments[ra][axisId]) || ""
      });
    });
  });

  return {
    classId,
    date: draft.date,
    momentId: draft.momentId,
    cells,
    cleared: [],
    absences
  };
}
